// src/pages/HelpDeskReportPage.js

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { auth, db } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    query,
    orderBy,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    where
} from 'firebase/firestore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import useFirestore from '../../hooks/useFirestore';
import {
    FormModal,
    ConfirmModal,
    PageHeader,
    Toast,
    Pagination
} from '../../components/common';
import styles from '../../modules/Registers/Registers.module.css';

// Collections
const TICKETS_COLLECTION = 'help-desk-tickets';
const REPORT_HISTORY_COLLECTION = 'help-desk-report-history';

// Constants
const ITEMS_PER_PAGE = 10;

const STATUS_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'Aberto', label: 'Aberto' },
    { value: 'Em Progresso', label: 'Em Progresso' },
    { value: 'Fechado', label: 'Fechado' },
    { value: 'Resolvido', label: 'Resolvido' },
];

const PRIORITY_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'Crítico', label: 'Crítico' },
    { value: 'Alta', label: 'Alta' },
    { value: 'Média', label: 'Média' },
    { value: 'Baixa', label: 'Baixa' },
];

const CATEGORY_OPTIONS = [
    { value: '', label: 'Todas' },
    { value: 'acesso', label: 'Acesso e Permissões' },
    { value: 'sistema', label: 'Erro no Sistema' },
    { value: 'duvida', label: 'Dúvida sobre Funcionalidade' },
    { value: 'melhoria', label: 'Sugestão de Melhoria' },
    { value: 'dados', label: 'Problema com Dados' },
    { value: 'integracao', label: 'Integração' },
    { value: 'hardware', label: 'Problema de Hardware' },
    { value: 'rede', label: 'Problema de Rede' },
    { value: 'seguranca', label: 'Segurança' },
    { value: 'outro', label: 'Outro' },
];

const URGENCY_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'imediato', label: 'Imediato' },
    { value: 'urgente', label: 'Urgente' },
    { value: 'normal', label: 'Normal' },
    { value: 'baixa', label: 'Baixa' },
];

const IMPACT_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'todos', label: 'Todos os usuários' },
    { value: 'departamento', label: 'Departamento' },
    { value: 'equipe', label: 'Equipe' },
    { value: 'individual', label: 'Individual' },
];

const HelpDeskReportPage = () => {
    // Report state
    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        searchTerm: '',
        status: '',
        priority: '',
        category: '',
        urgency: '',
        impact: '',
        assignedToEmail: '',
        dateFrom: '',
        dateTo: '',
        hasFollowUp: false,
        minTimeSpent: '',
        maxTimeSpent: '',
    });

    // Pagination for main list
    const [currentPage, setCurrentPage] = useState(1);

    // Fetch tickets
    const { data: tickets, loading: ticketsLoading, error: ticketsError } = useFirestore(
        TICKETS_COLLECTION,
        'createdAt',
        'desc'
    );

    // Get current user
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    // Load report history
    useEffect(() => {
        loadReports();
    }, []);

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    const loadReports = async () => {
        setLoadingReports(true);
        try {
            const q = query(
                collection(db, REPORT_HISTORY_COLLECTION),
                orderBy('generatedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const reportsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                generatedAt: doc.data().generatedAt?.toDate() || new Date()
            }));
            setReports(reportsList);
        } catch (error) {
            console.error("Error loading report history:", error);
            addToast('Erro ao carregar histórico', 'error');
        } finally {
            setLoadingReports(false);
        }
    };

    const handleDeleteClick = (reportId) => {
        setReportToDelete(reportId);
        setIsConfirmOpen(true);
    };

    const deleteReport = async () => {
        if (!reportToDelete) return;

        try {
            await deleteDoc(doc(db, REPORT_HISTORY_COLLECTION, reportToDelete));
            setReports(reports.filter(r => r.id !== reportToDelete));
            addToast('Relatório excluído com sucesso', 'success');
        } catch (error) {
            console.error("Error deleting report:", error);
            addToast('Erro ao excluir relatório', 'error');
        } finally {
            setIsConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    // Helper functions
    const getStatusLabel = (value) => {
        return STATUS_OPTIONS.find(s => s.value === value)?.label || value || '-';
    };

    const getPriorityLabel = (value) => {
        const priorityMap = {
            'critico': 'Crítico',
            'alto': 'Alta',
            'media': 'Média',
            'medio': 'Média',
            'baixo': 'Baixa',
        };
        const mapped = priorityMap[value?.toLowerCase()] || value;
        return PRIORITY_OPTIONS.find(p => p.value === mapped)?.label || mapped || '-';
    };

    const getCategoryLabel = (value) => {
        return CATEGORY_OPTIONS.find(c => c.value === value)?.label || value || '-';
    };

    const getUrgencyLabel = (value) => {
        return URGENCY_OPTIONS.find(u => u.value === value)?.label || value || '-';
    };

    const getImpactLabel = (value) => {
        return IMPACT_OPTIONS.find(i => i.value === value)?.label || value || '-';
    };

    const getTimeInStatus = (ticket) => {
        if (!ticket?.updatedAt?.toDate) return 'N/A';
        const diff = Date.now() - ticket.updatedAt.toDate().getTime();
        const minutes = Math.floor(diff / 60000);
        if (minutes < 1) return '< 1 min';
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ${minutes % 60}min`;
        const days = Math.floor(hours / 24);
        return `${days}d ${hours % 24}h`;
    };

    const formatTimeSpent = (minutes) => {
        if (!minutes || minutes < 0) return '0min';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
        }
        return `${mins}min`;
    };

    // Apply filters to tickets
    const filteredTickets = useMemo(() => {
        if (!tickets) return [];

        let filtered = [...tickets];

        // Search term
        if (filters.searchTerm) {
            const term = filters.searchTerm.toLowerCase();
            filtered = filtered.filter(ticket =>
                (ticket.id || '').toLowerCase().includes(term) ||
                (ticket.assunto || '').toLowerCase().includes(term) ||
                (ticket.descricao || '').toLowerCase().includes(term) ||
                (ticket.userName || '').toLowerCase().includes(term) ||
                (ticket.userEmail || '').toLowerCase().includes(term)
            );
        }

        // Filter by status
        if (filters.status) {
            filtered = filtered.filter(ticket => ticket.status === filters.status);
        }

        // Filter by priority
        if (filters.priority) {
            filtered = filtered.filter(ticket => {
                const priorityMap = {
                    'Crítico': ['critico', 'critical'],
                    'Alta': ['alto', 'high'],
                    'Média': ['medio', 'media', 'medium'],
                    'Baixa': ['baixo', 'low'],
                };
                const priorityValues = priorityMap[filters.priority] || [filters.priority.toLowerCase()];
                const ticketPriority = ticket.prioridadeRaw?.toLowerCase() || ticket.prioridade?.toLowerCase() || '';
                return priorityValues.some(p => ticketPriority.includes(p));
            });
        }

        // Filter by category
        if (filters.category) {
            filtered = filtered.filter(ticket => ticket.categoria === filters.category);
        }

        // Filter by urgency
        if (filters.urgency) {
            filtered = filtered.filter(ticket => ticket.urgencia === filters.urgency);
        }

        // Filter by impact
        if (filters.impact) {
            filtered = filtered.filter(ticket => ticket.impacto === filters.impact);
        }

        // Filter by assigned user
        if (filters.assignedToEmail) {
            filtered = filtered.filter(ticket =>
                ticket.assignedToEmail?.toLowerCase().includes(filters.assignedToEmail.toLowerCase())
            );
        }

        // Filter by has follow-up
        if (filters.hasFollowUp) {
            filtered = filtered.filter(ticket => ticket.followUps && ticket.followUps.length > 0);
        }

        // Filter by time spent range
        if (filters.minTimeSpent) {
            const min = parseFloat(filters.minTimeSpent);
            if (!isNaN(min)) {
                filtered = filtered.filter(ticket => (ticket.timeSpentMinutes || 0) >= min);
            }
        }

        if (filters.maxTimeSpent) {
            const max = parseFloat(filters.maxTimeSpent);
            if (!isNaN(max)) {
                filtered = filtered.filter(ticket => (ticket.timeSpentMinutes || 0) <= max);
            }
        }

        // Filter by creation date range
        if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(ticket => {
                const createdAt = ticket.createdAt?.toDate ? ticket.createdAt.toDate() : new Date(ticket.createdAt);
                return createdAt && createdAt >= fromDate;
            });
        }

        if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(ticket => {
                const createdAt = ticket.createdAt?.toDate ? ticket.createdAt.toDate() : new Date(ticket.createdAt);
                return createdAt && createdAt <= toDate;
            });
        }

        // Sort by createdAt (newest first)
        filtered.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return dateB - dateA;
        });

        return filtered;
    }, [tickets, filters]);

    // Pagination for main list
    const totalPages = Math.ceil((filteredTickets?.length || 0) / ITEMS_PER_PAGE);
    const currentTickets = (filteredTickets || []).slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    // Get unique assigned users for filter
    const uniqueAssignedUsers = useMemo(() => {
        if (!tickets) return [];
        const users = new Set();
        tickets.forEach(ticket => {
            if (ticket.assignedToEmail) users.add(ticket.assignedToEmail);
        });
        return Array.from(users);
    }, [tickets]);

    const generateExcelReport = async (items, filtersObj, reportTitle, fileName) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Tickets');

        // Title row
        worksheet.mergeCells('A1:M1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = reportTitle;
        titleCell.font = { size: 18, bold: true, color: { argb: 'FF2C3E50' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(1).height = 35;

        // Generation info
        worksheet.mergeCells('A2:M2');
        worksheet.getCell('A2').value = `Gerado em: ${new Date().toLocaleString('pt-BR')}`;
        worksheet.getCell('A2').font = { size: 10, italic: true };
        worksheet.getCell('A2').alignment = { horizontal: 'center' };
        worksheet.getRow(2).height = 22;

        worksheet.mergeCells('A3:M3');
        worksheet.getCell('A3').value = `Gerado por: ${currentUser?.email || 'Usuário'}`;
        worksheet.getCell('A3').font = { size: 10, italic: true };
        worksheet.getCell('A3').alignment = { horizontal: 'center' };
        worksheet.getRow(3).height = 22;

        // Headers
        const headers = [
            'ID', 'Status', 'Prioridade', 'Categoria', 'Urgência', 'Impacto',
            'Solicitante', 'Assunto', 'Descrição', 'Responsável',
            'Tempo Gasto', 'Tempo em Status', 'Mensagens'
        ];

        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF2C3E50' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
        worksheet.getRow(4).height = 40;

        // Set column widths
        worksheet.getColumn(1).width = 12;
        worksheet.getColumn(2).width = 14;
        worksheet.getColumn(3).width = 12;
        worksheet.getColumn(4).width = 22;
        worksheet.getColumn(5).width = 12;
        worksheet.getColumn(6).width = 18;
        worksheet.getColumn(7).width = 20;
        worksheet.getColumn(8).width = 35;
        worksheet.getColumn(9).width = 50;
        worksheet.getColumn(10).width = 20;
        worksheet.getColumn(11).width = 14;
        worksheet.getColumn(12).width = 16;
        worksheet.getColumn(13).width = 12;

        // Data rows
        items.forEach((ticket) => {
            const priorityLabel = getPriorityLabel(ticket.prioridadeRaw || ticket.prioridade);
            const row = worksheet.addRow([
                ticket.id.substring(0, 8) || '-',
                ticket.status || '-',
                priorityLabel,
                getCategoryLabel(ticket.categoria),
                getUrgencyLabel(ticket.urgencia),
                getImpactLabel(ticket.impacto),
                ticket.userName || '-',
                ticket.assunto || '-',
                ticket.descricao || '-',
                ticket.assignedToName || 'Não atribuído',
                formatTimeSpent(ticket.timeSpentMinutes),
                getTimeInStatus(ticket),
                ticket.followUps?.length || 0
            ]);

            row.eachCell((cell, colNumber) => {
                cell.alignment = { vertical: 'middle', wrapText: true };
                if (colNumber !== 8 && colNumber !== 9) {
                    cell.alignment = { ...cell.alignment, horizontal: 'center' };
                }
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            row.height = 35;

            // Color status cell
            const statusCell = row.getCell(2);
            if (ticket.status === 'Fechado' || ticket.status === 'Resolvido') {
                statusCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFd4edda' }
                };
            } else if (ticket.status === 'Em Progresso') {
                statusCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFFFF3CD' }
                };
            } else if (ticket.status === 'Aberto') {
                statusCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFF8D7DA' }
                };
            }

            // Color priority cell
            const priorityCell = row.getCell(3);
            if (priorityLabel === 'Crítico') {
                priorityCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFF8D7DA' }
                };
                priorityCell.font = { color: { argb: 'FFC62828' } };
            } else if (priorityLabel === 'Alta') {
                priorityCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFFFE0B2' }
                };
            } else if (priorityLabel === 'Média') {
                priorityCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFFFF3CD' }
                };
            } else if (priorityLabel === 'Baixa') {
                priorityCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFD4EDDA' }
                };
            }
        });

        // Summary row
        const summaryRow = worksheet.addRow([`Total de tickets: ${items.length}`, '', '', '', '', '', '', '', '', '', '', '', '']);
        summaryRow.getCell(1).font = { bold: true };
        worksheet.mergeCells(`A${worksheet.rowCount}:M${worksheet.rowCount}`);
        worksheet.getRow(worksheet.rowCount).height = 28;

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    const generateReport = async () => {
        if (!currentUser) {
            addToast('Você precisa estar logado para gerar relatórios', 'error');
            return;
        }

        if (filteredTickets.length === 0) {
            addToast('Nenhum ticket encontrado com os filtros selecionados.', 'warning');
            return;
        }

        setGenerating(true);
        addToast('Gerando relatório...', 'info');

        try {
            const filterDisplay = {};

            if (filters.status) {
                filterDisplay.statusLabel = getStatusLabel(filters.status);
            }
            if (filters.priority) {
                filterDisplay.priorityLabel = filters.priority;
            }
            if (filters.category) {
                filterDisplay.categoryLabel = getCategoryLabel(filters.category);
            }
            if (filters.urgency) {
                filterDisplay.urgencyLabel = getUrgencyLabel(filters.urgency);
            }
            if (filters.impact) {
                filterDisplay.impactLabel = getImpactLabel(filters.impact);
            }
            if (filters.assignedToEmail) {
                filterDisplay.assignedToEmail = filters.assignedToEmail;
                const ticketWithUser = tickets?.find(t => t.assignedToEmail === filters.assignedToEmail);
                if (ticketWithUser?.assignedToName) {
                    filterDisplay.assignedToName = ticketWithUser.assignedToName;
                }
            }
            if (filters.dateFrom) {
                filterDisplay.dateFrom = new Date(filters.dateFrom).toLocaleDateString('pt-BR');
            }
            if (filters.dateTo) {
                filterDisplay.dateTo = new Date(filters.dateTo).toLocaleDateString('pt-BR');
            }
            if (filters.searchTerm) {
                filterDisplay.searchTerm = filters.searchTerm;
            }
            if (filters.hasFollowUp) {
                filterDisplay.hasFollowUp = 'Sim';
            }
            if (filters.minTimeSpent) {
                filterDisplay.minTimeSpent = filters.minTimeSpent;
            }
            if (filters.maxTimeSpent) {
                filterDisplay.maxTimeSpent = filters.maxTimeSpent;
            }

            let reportTitle = 'RELATÓRIO DE TICKETS HELP DESK';
            if (filterDisplay.statusLabel && filterDisplay.statusLabel !== 'Todos') {
                reportTitle += ` - ${filterDisplay.statusLabel}`;
            }
            if (filterDisplay.priorityLabel && filterDisplay.priorityLabel !== 'Todos') {
                reportTitle += ` - ${filterDisplay.priorityLabel}`;
            }

            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filterParts = [];
            if (filterDisplay.statusLabel && filterDisplay.statusLabel !== 'Todos') filterParts.push(filterDisplay.statusLabel.replace(/\s/g, '_'));
            if (filterDisplay.priorityLabel && filterDisplay.priorityLabel !== 'Todos') filterParts.push(filterDisplay.priorityLabel.replace(/\s/g, '_'));
            const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
            const fileName = `Relatorio_Tickets${filterSuffix}_${timestamp}.xlsx`;

            await generateExcelReport(filteredTickets, filterDisplay, reportTitle, fileName);

            // Count tickets by status for summary
            const statusCount = {};
            filteredTickets.forEach(t => {
                statusCount[t.status] = (statusCount[t.status] || 0) + 1;
            });

            const reportData = {
                fileName,
                generatedAt: serverTimestamp(),
                generatedBy: currentUser.email || currentUser.displayName || currentUser.uid,
                generatedByEmail: currentUser.email,
                generatedByName: currentUser.displayName || '',
                ticketCount: filteredTickets.length,
                statusSummary: statusCount,
                filters: {
                    searchTerm: filters.searchTerm || null,
                    status: filters.status,
                    statusLabel: filterDisplay.statusLabel || null,
                    priority: filters.priority,
                    priorityLabel: filterDisplay.priorityLabel || null,
                    category: filters.category,
                    categoryLabel: filterDisplay.categoryLabel || null,
                    urgency: filters.urgency,
                    urgencyLabel: filterDisplay.urgencyLabel || null,
                    impact: filters.impact,
                    impactLabel: filterDisplay.impactLabel || null,
                    assignedToEmail: filters.assignedToEmail || null,
                    assignedToName: filterDisplay.assignedToName || null,
                    dateFrom: filters.dateFrom || null,
                    dateTo: filters.dateTo || null,
                    hasFollowUp: filters.hasFollowUp || false,
                    minTimeSpent: filters.minTimeSpent || null,
                    maxTimeSpent: filters.maxTimeSpent || null
                }
            };

            const docRef = await addDoc(collection(db, REPORT_HISTORY_COLLECTION), reportData);

            setReports([
                {
                    id: docRef.id,
                    ...reportData,
                    generatedAt: new Date()
                },
                ...reports
            ]);

            addToast(`Relatório "${fileName}" gerado com sucesso!`, 'success');
            setIsModalOpen(false);

        } catch (error) {
            console.error('Error generating report:', error);
            addToast(`Erro ao gerar relatório: ${error.message}`, 'error');
        } finally {
            setGenerating(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters({
            searchTerm: '',
            status: '',
            priority: '',
            category: '',
            urgency: '',
            impact: '',
            assignedToEmail: '',
            dateFrom: '',
            dateTo: '',
            hasFollowUp: false,
            minTimeSpent: '',
            maxTimeSpent: '',
        });
        setCurrentPage(1);
    };

    const formatDate = (date) => {
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFilterDescription = (filtersObj) => {
        if (!filtersObj) return '-';
        const parts = [];
        if (filtersObj.statusLabel && filtersObj.statusLabel !== 'Todos') parts.push(`Status: ${filtersObj.statusLabel}`);
        if (filtersObj.priorityLabel && filtersObj.priorityLabel !== 'Todos') parts.push(`Prioridade: ${filtersObj.priorityLabel}`);
        if (filtersObj.categoryLabel && filtersObj.categoryLabel !== 'Todas') parts.push(`Categoria: ${filtersObj.categoryLabel}`);
        if (filtersObj.urgencyLabel && filtersObj.urgencyLabel !== 'Todos') parts.push(`Urgência: ${filtersObj.urgencyLabel}`);
        if (filtersObj.impactLabel && filtersObj.impactLabel !== 'Todos') parts.push(`Impacto: ${filtersObj.impactLabel}`);
        if (filtersObj.assignedToName) parts.push(`Responsável: ${filtersObj.assignedToName}`);
        if (filtersObj.dateFrom) parts.push(`De: ${filtersObj.dateFrom}`);
        if (filtersObj.dateTo) parts.push(`Até: ${filtersObj.dateTo}`);
        if (filtersObj.searchTerm) parts.push(`Busca: ${filtersObj.searchTerm}`);
        if (filtersObj.hasFollowUp) parts.push('Com mensagens');
        if (filtersObj.minTimeSpent) parts.push(`Tempo ≥ ${filtersObj.minTimeSpent}min`);
        if (filtersObj.maxTimeSpent) parts.push(`Tempo ≤ ${filtersObj.maxTimeSpent}min`);
        if (parts.length === 0) return 'Todos os tickets';
        return parts.join(', ');
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        handleResetFilters();
    };

    const isLoading = ticketsLoading;

    // Check if any filter is active
    const hasActiveFilters = filters.status || filters.priority || filters.category ||
        filters.urgency || filters.impact || filters.assignedToEmail ||
        filters.dateFrom || filters.dateTo || filters.searchTerm ||
        filters.hasFollowUp || filters.minTimeSpent || filters.maxTimeSpent;

    // Render filter form content for modal
    const renderFilterForm = () => (
        <>
            {/* Search Term */}
            <div className={styles.formGroup}>
                <label>Busca por ID, Assunto, Descrição ou Solicitante</label>
                <input
                    type="text"
                    placeholder="Digite para buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    className={styles.input}
                />
            </div>

            {/* Basic Filters Row */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Status</label>
                    <select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className={styles.select}
                    >
                        {STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Prioridade</label>
                    <select
                        value={filters.priority}
                        onChange={(e) => handleFilterChange('priority', e.target.value)}
                        className={styles.select}
                    >
                        {PRIORITY_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Categoria</label>
                    <select
                        value={filters.category}
                        onChange={(e) => handleFilterChange('category', e.target.value)}
                        className={styles.select}
                    >
                        {CATEGORY_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Urgency & Impact */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Urgência</label>
                    <select
                        value={filters.urgency}
                        onChange={(e) => handleFilterChange('urgency', e.target.value)}
                        className={styles.select}
                    >
                        {URGENCY_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Impacto</label>
                    <select
                        value={filters.impact}
                        onChange={(e) => handleFilterChange('impact', e.target.value)}
                        className={styles.select}
                    >
                        {IMPACT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Responsável</label>
                    <select
                        value={filters.assignedToEmail}
                        onChange={(e) => handleFilterChange('assignedToEmail', e.target.value)}
                        className={styles.select}
                    >
                        <option value="">Todos os responsáveis</option>
                        {uniqueAssignedUsers.map(email => (
                            <option key={email} value={email}>{email}</option>
                        ))}
                        <option value="nao_atribuido">Não atribuído</option>
                    </select>
                </div>
            </div>

            {/* Time Spent Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Tempo Gasto Mínimo (minutos)</label>
                    <input
                        type="number"
                        step="1"
                        min="0"
                        placeholder="Ex: 30"
                        value={filters.minTimeSpent}
                        onChange={(e) => handleFilterChange('minTimeSpent', e.target.value)}
                        className={styles.input}
                    />
                </div>

                <div className={styles.formGroup}>
                    <label>Tempo Gasto Máximo (minutos)</label>
                    <input
                        type="number"
                        step="1"
                        min="0"
                        placeholder="Ex: 120"
                        value={filters.maxTimeSpent}
                        onChange={(e) => handleFilterChange('maxTimeSpent', e.target.value)}
                        className={styles.input}
                    />
                </div>

                <div className={styles.formGroup}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '28px' }}>
                        <input
                            type="checkbox"
                            checked={filters.hasFollowUp}
                            onChange={(e) => handleFilterChange('hasFollowUp', e.target.checked)}
                        />
                        Apenas com mensagens
                    </label>
                </div>
            </div>

            {/* Date Range Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Data de Criação (Início)</label>
                    <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className={styles.input}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Data de Criação (Fim)</label>
                    <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className={styles.input}
                    />
                </div>
            </div>

            {/* Preview Info */}
            <div className={styles.previewInfo}>
                📊 {filteredTickets.length} {filteredTickets.length === 1 ? 'ticket encontrado' : 'tickets encontrados'} com os filtros atuais
                {filteredTickets.length > 0 && (
                    <span style={{ marginLeft: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        ({filteredTickets.filter(t => t.status === 'Aberto').length} abertos,{' '}
                        {filteredTickets.filter(t => t.status === 'Em Progresso').length} em progresso,{' '}
                        {filteredTickets.filter(t => t.status === 'Fechado' || t.status === 'Resolvido').length} fechados)
                    </span>
                )}
            </div>
        </>
    );

    return (
        <div className={styles.pageContainer}>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={deleteReport}
                title="Excluir Relatório"
                message="Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita."
                confirmText="Excluir"
                danger
            />

            {/* Filter Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={generateReport}
                title="Gerar Relatório de Tickets Help Desk"
                subtitle="Selecione os filtros desejados para o relatório"
                isLoading={generating}
                submitText={`Gerar Relatório (${filteredTickets.length})`}
                cancelText="Cancelar"
                maxWidth="860px"
            >
                {renderFilterForm()}
                <div className={styles.formActions} style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                    <button type="button" onClick={handleResetFilters} className={styles.cancelButton}>
                        Limpar Filtros                    </button>
                </div>
            </FormModal>

            {/* Main Header */}
            <PageHeader
                title="Relatório de Tickets Help Desk"
                actionText="Gerar Relatório"
                onAction={openModal}
                disabled={!currentUser || isLoading}
            />

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <div className={styles.activeFiltersContainer}>
                    <span className={styles.activeFiltersLabel}>Filtros ativos:</span>
                    {filters.searchTerm && (
                        <span className={styles.filterBadge}>
                            🔍 Busca: {filters.searchTerm}
                        </span>
                    )}
                    {filters.status && (
                        <span className={styles.filterBadge}>
                            📋 Status: {getStatusLabel(filters.status)}
                        </span>
                    )}
                    {filters.priority && (
                        <span className={styles.filterBadge}>
                            ⚡ Prioridade: {filters.priority}
                        </span>
                    )}
                    {filters.category && (
                        <span className={styles.filterBadge}>
                            🏷️ Categoria: {getCategoryLabel(filters.category)}
                        </span>
                    )}
                    {filters.urgency && (
                        <span className={styles.filterBadge}>
                            🔥 Urgência: {getUrgencyLabel(filters.urgency)}
                        </span>
                    )}
                    {filters.impact && (
                        <span className={styles.filterBadge}>
                            📊 Impacto: {getImpactLabel(filters.impact)}
                        </span>
                    )}
                    {filters.assignedToEmail && (
                        <span className={styles.filterBadge}>
                            👤 Responsável: {filters.assignedToEmail === 'nao_atribuido' ? 'Não atribuído' : filters.assignedToEmail}
                        </span>
                    )}
                    {filters.hasFollowUp && (
                        <span className={styles.filterBadge}>
                            💬 Com mensagens
                        </span>
                    )}
                    {filters.minTimeSpent && (
                        <span className={styles.filterBadge}>
                            ⏱️ Tempo ≥ {filters.minTimeSpent}min
                        </span>
                    )}
                    {filters.maxTimeSpent && (
                        <span className={styles.filterBadge}>
                            ⏱️ Tempo ≤ {filters.maxTimeSpent}min
                        </span>
                    )}
                    {filters.dateFrom && (
                        <span className={styles.filterBadge}>
                            📅 De: {new Date(filters.dateFrom).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                    {filters.dateTo && (
                        <span className={styles.filterBadge}>
                            📅 Até: {new Date(filters.dateTo).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                </div>
            )}

            

            {/* Report History Section */}
            <div className={`${styles.tableContainer} ${styles.historyTable}`}>
                <h3 className={styles.tableTitle}>
                    Histórico de Relatórios Gerados
                </h3>
                <div style={{ overflowX: 'auto' }}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Data/Hora</th>
                                <th>Nome do Arquivo</th>
                                <th>Gerado por</th>
                                <th>Tickets</th>
                                <th>Filtros Aplicados</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingReports ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        <div className={styles.spinner} style={{ margin: '0 auto 8px auto' }}></div>
                                        <span>Carregando histórico...</span>
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        Nenhum relatório gerado ainda. Clique em "Gerar Relatório" para começar.
                                    </td>
                                </tr>
                            ) : (
                                reports.map((report) => (
                                    <tr key={report.id} className={styles.tableRow}>
                                        <td>{formatDate(report.generatedAt)}</td>
                                        <td>
                                            <span className={styles.fileName}>
                                                {report.fileName}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.userInfo}>
                                                <span className={styles.userName}>
                                                    {report.generatedByName || report.generatedBy?.split('@')[0] || 'Usuário'}
                                                </span>
                                                {report.generatedByEmail && (
                                                    <span className={styles.userEmail}>
                                                        {report.generatedByEmail}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                            {report.ticketCount}
                                            {report.statusSummary && (
                                                <div style={{ fontSize: '11px', fontWeight: 'normal', color: 'var(--text-secondary)' }}>
                                                    {Object.entries(report.statusSummary).map(([status, count]) => (
                                                        <span key={status}>{status}: {count} </span>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td className={styles.filterDescription}>
                                            {getFilterDescription(report.filters)}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleDeleteClick(report.id)}
                                                className={`${styles.actionButton} ${styles.deleteButton} ${styles.smallDeleteButton}`}
                                            >
                                                Excluir
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={filteredTickets?.length || 0}
                        onPageChange={setCurrentPage}
                        itemsLabel="tickets"
                    />
                )}
            </div>
        </div>
    );
};

export default HelpDeskReportPage;